import { Injectable } from '@angular/core';
import { IEmployee } from '../models/employee/employee.model';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, lastValueFrom } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { tap } from 'rxjs/operators';
import { IHoliday, IPrivateHoliday } from '../models/holiday/holiday.model';
import { IFieldOffice } from '../models/field-office/field-office.model';
import { IBusinessHours } from '../models/business-hours/business-hours.model';
import { IEmployeeGroup } from '../models/employee-group/employee-group.model';
import { GlobalFunction } from '../global-function';
import { IAuthorizationRole } from '../models/authorization-role/authorization-role.model';
import { IService } from '../models/service/service.model';
import { IChangePassword } from '../models/employee/change-password.model';
import { EmployeeAppointmentNoteDto, IAppointment } from '../models/appointment/appointment.model';
import { NameIdDto } from '../models/basic/name-id-dto.model';
import { QFlexUser } from '../models/qflex/q-flex-user.model';
import * as moment from 'moment';
import { AppointmentPropertyDisplayOptionDto } from '../models/appointment/appointment-poperty-display-option-dto.model';

@Injectable({
    providedIn: 'root'
})
export class EmployeeService
{

    private services: IService[];   // used for the employee-dialog when selecting / removing services

    constructor( private http: HttpClient ) { }

    public getAllEmployees(): Observable<IEmployee[]>
    {
        return this.http.get<IEmployee[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get-all");
    }

    public getOverviewVM(): Observable<any>
    {
        const apiCalls = [
            this.http.get<IEmployee[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get-all"),
            this.http.get<IFieldOffice[]>(AppConfigService.appConfig.WebAPIServer + "/api/field-office/get-all/"),
            this.http.get<IEmployeeGroup[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee-group/get-all/"),
            this.http.get<IService[]>(AppConfigService.appConfig.WebAPIServer + "/api/service/get-all/")
        ];
        return forkJoin(apiCalls);
    }

    public getMyAppointmentColumnOptions(id, listType: number): Observable<AppointmentPropertyDisplayOptionDto>
    {
        return this.http.get<AppointmentPropertyDisplayOptionDto>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get-appointment-list-column-options/" + id + "/type/" + listType);
    }

    public setMyAppointmentColumnOptions(id: string, options: AppointmentPropertyDisplayOptionDto)
    {
        return this.http.put<AppointmentPropertyDisplayOptionDto>(AppConfigService.appConfig.WebAPIServer + "/api/employee/set-appointment-list-column-options/" + id, options)
    }


    public async addEmployeeAppointmentNoteAsync(dto: EmployeeAppointmentNoteDto): Promise<string>
    {
        return await lastValueFrom(this.http.post<string>(AppConfigService.appConfig.WebAPIServer + "/api/employee/appointment/add-Note", dto));
    }

    public async updateEmployeeAppointmentNoteAsync(dto: EmployeeAppointmentNoteDto): Promise<void>
    {
        return await lastValueFrom(this.http.post<void>(AppConfigService.appConfig.WebAPIServer + "/api/employee/appointment/update-Note", dto));
    }

    public async deleteEmployeeAppointmentNoteAsync(id: string): Promise<void>
    {
        return await lastValueFrom(this.http.post<void>(AppConfigService.appConfig.WebAPIServer + "/api/employee/appointment/delete-Note/" + id, id));
    }

    public get(id: string): Observable<IEmployee>
    {
        return this.http.get<IEmployee>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get/" + id);
    }

    public async getAsync(id: string): Promise<IEmployee>
    {
        return await lastValueFrom(this.http.get<IEmployee>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get/" + id));
    }

    public getDetailsVM(id: string): Observable<any>
    {
        const apiCalls = [
            this.http.get<IEmployee>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get/" + id),
            this.http.get<IService[]>(AppConfigService.appConfig.WebAPIServer + "/api/service/get-all/").pipe(
                tap(data => this.services = data),
            ),
            this.http.get<IFieldOffice[]>(AppConfigService.appConfig.WebAPIServer + "/api/field-office/get-all/"),
            this.http.get<IBusinessHours>(AppConfigService.appConfig.WebAPIServer + "/api/business-hours/get-all/"),
            this.http.get<IHoliday[]>(AppConfigService.appConfig.WebAPIServer + "/api/holiday/get-all/"),
            this.http.get<IEmployeeGroup[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee-group/get-all/"),
            this.http.get<IAuthorizationRole[]>(AppConfigService.appConfig.WebAPIServer + "/api/authorization-roles/get-all"),
            this.http.get<IEmployee[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get-all/"),
            this.http.get<boolean>(AppConfigService.appConfig.WebAPIServer + "/api/getEnableIntegrationServer"),
        ]
        return forkJoin(apiCalls);
    }

    
    public getCreationVM(): Observable<any>
    {
        const apiCalls = [
            this.http.get<IService[]>(AppConfigService.appConfig.WebAPIServer + "/api/service/get-all").pipe(
                tap(data => this.services = data),
            ),
            this.http.get<IFieldOffice[]>(AppConfigService.appConfig.WebAPIServer + "/api/field-office/get-all/"),
            this.http.get<IBusinessHours[]>(AppConfigService.appConfig.WebAPIServer + "/api/business-hours/get-all/"),
            this.http.get<IHoliday[]>(AppConfigService.appConfig.WebAPIServer + "/api/holiday/get-all/"),
            this.http.get<IEmployeeGroup[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee-group/get-all/"),
            this.http.get<IAuthorizationRole[]>(AppConfigService.appConfig.WebAPIServer + "/api/authorization-roles/get-all"),
            this.http.get<IEmployee[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/get-all/"),
            this.http.get<NameIdDto[]>(AppConfigService.appConfig.WebAPIServer + "/api/qflex/get-all/employees"),
            this.http.get<boolean>(AppConfigService.appConfig.WebAPIServer + "/api/getEnableIntegrationServer"),
        ];
        return forkJoin(apiCalls);
    }

    public getQFlexUser(id: string): Observable<QFlexUser>
    {
        return this.http.get<QFlexUser>(AppConfigService.appConfig.WebAPIServer + "/api/qflex/get/user/" + id);
    }

    public getQFlexEmployeesForFieldOffice(fieldOfficeId): Observable<NameIdDto[]>
    {
        return this.http.get<NameIdDto[]>(AppConfigService.appConfig.WebAPIServer + "/api/qflex/get-all/employees/" + fieldOfficeId);
    }

    public create(employee: IEmployee): Observable<IEmployee>
    {
        employee.languageCode = "de";

        if (employee.password === "")
        {
            employee.password = null;
            employee.passwordRepeated = null;
        }

        // id values required.
        employee.id = GlobalFunction.emptyGuid();

        // if (employee.employeeGroups == ["0"])
        // {
        //     employee.employeeGroups = [];
        // }

        if(GlobalFunction.isEmpty(employee.qFlexUserId))
        {
            employee.qFlexUserId = GlobalFunction.emptyGuid();
        }

        employee.privateHolidays.forEach(privateHoliday => {
            privateHoliday.from = moment(privateHoliday.from).format();
            privateHoliday.until = moment(privateHoliday.until).format();
        });

        employee.privateAvailabilities.forEach(privateAvailability => {
            privateAvailability.from = moment(privateAvailability.from).format();
            privateAvailability.until = moment(privateAvailability.until).format();
        });

        return this.http.post<IEmployee>(AppConfigService.appConfig.WebAPIServer + "/api/employee/create", employee);
    }


    public update(employee: IEmployee): Observable<IAppointment[]>
    {
        // if (employee.employeeGroups == ["0"])
        // {
        //     employee.employeeGroups = [];
        //  }
 
        if(GlobalFunction.isEmpty(employee.password))
        {
            employee.password = null;
        }
        if(GlobalFunction.isEmpty(employee.passwordRepeated))
        {
            employee.passwordRepeated = null;
        }
        if(GlobalFunction.isEmpty(employee.qFlexUserId))
        {
            employee.qFlexUserId = GlobalFunction.emptyGuid();
        }

        employee.privateHolidays.forEach(privateHoliday => {
            privateHoliday.from = moment(privateHoliday.from).format();
            privateHoliday.until = moment(privateHoliday.until).format();
        });

        employee.privateAvailabilities.forEach(privateAvailability => {
            privateAvailability.from = moment(privateAvailability.from).format();
            privateAvailability.until = moment(privateAvailability.until).format();
        });

        return this.http.put<IAppointment[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/update", employee);
    }

    
    public setAllActive(active: boolean): Observable<IAppointment[]>
    {
        return this.http.put<IAppointment[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/setAllActive/" + active, null);
    }

    public setActive(employee: IEmployee): Observable<IAppointment[]>
    {
        return this.http.put<IAppointment[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/setActive/" + employee.isActive + "/id/" + employee.id, null);
    }


    public delete(id: string): Observable<IAppointment[]>
    {
        // // ein post benoetigt auch etwas im body, aber an sich muss gar kein body mitgeschickt werden.
        return this.http.post<IAppointment[]>(AppConfigService.appConfig.WebAPIServer + "/api/employee/delete/" + id , id);
    }



    public changePassword(password: IChangePassword): Observable<any> 
    {
        return this.http.put(AppConfigService.appConfig.WebAPIServer + "/api/employee/change-password", password);
    }

    // used for the employee-dialog when selecting / removing services
    public getServiceWithId(guid: string): IService
    {
        let service = this.services.find(x => x.id === guid);
        return service;
    }

    
    // used in the employee-dialog.component.html
    public getServiceDurationValuesFromId(guid: string): string
    {
        let result = "";
        let index = this.services.findIndex(x => x.id === guid);
        if (index !== -1)
        {
            result = ' (' + this.services[index].initialDuration.toString() + '/' + this.services[index].consecutiveDuration.toString() + ') ';
            return (result);
        }
        return "";
    }


    public recalcalculate(id: string): Observable<IEmployee>
    {
        return this.http.post<IEmployee>(AppConfigService.appConfig.WebAPIServer + "/api/employee/recalculate/employee-id/" + id, id);
    }



    
}